import $ from 'jquery';

interface Organization {
    id: string;
    name: string;
}

interface OrgSelectionCache {
    organizationId: string;
    organizationName: string;
    timestamp: number;
    auth0Domain: string;
    email: string;
}

// Cache helper functions
const CACHE_DURATION_MS = 30 * 1000; // 30 seconds

const getCacheKey = (auth0Domain: string, email: string): string => {
    return `ls_org_cache_${auth0Domain}_${email}`;
};

const getCachedOrgSelection = (auth0Domain: string, email: string): OrgSelectionCache | null => {
    try {
        const key = getCacheKey(auth0Domain, email);
        const cached = localStorage.getItem(key);
        if (!cached) return null;

        const parsed = JSON.parse(cached) as OrgSelectionCache;
        return parsed;
    } catch (e) {
        console.error('Error reading org selection cache:', e);
        return null;
    }
};

const setCachedOrgSelection = (
    auth0Domain: string,
    email: string,
    organizationId: string,
    organizationName: string
): void => {
    try {
        const key = getCacheKey(auth0Domain, email);
        const cache: OrgSelectionCache = {
            organizationId,
            organizationName,
            timestamp: Date.now(),
            auth0Domain,
            email
        };
        localStorage.setItem(key, JSON.stringify(cache));
    } catch (e) {
        console.error('Error saving org selection cache:', e);
    }
};

const isCacheValid = (
    cache: OrgSelectionCache | null,
    organizations: Organization[]
): boolean => {
    if (!cache) return false;

    const now = Date.now();
    const age = now - cache.timestamp;

    // Check if cache is < 30 seconds)
    if (age < 0 || age > CACHE_DURATION_MS) {
        console.log('Cache expired:', age, 'ms old');
        return false;
    }

    // Check if cached org still exists in available orgs
    const orgExists = organizations.some(org => org.id === cache.organizationId);
    if (!orgExists) {
        console.log('Cache invalid: org no longer available');
        return false;
    }

    return true;
};

const clearOrgCache = (auth0Domain: string, email: string): void => {
    try {
        const key = getCacheKey(auth0Domain, email);
        localStorage.removeItem(key);
    } catch (e) {
        console.error('Error clearing org cache:', e);
    }
};

const getInitials = (name: string): string => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const createOrgCardGrid = (containerSelector: string, host?: string) => {
    const $container = $(containerSelector);
    let selectedValue = '';
    let changeCallback: ((value: string) => void) | null = null;

    const renderCards = (organizations: Organization[]) => {
        $container.empty();
        selectedValue = '';

        organizations.forEach(org => {
            const initials = getInitials(org.name);

            // Build logo URL if host is provided
            const logoUrl = host ? `${host}/v1/public/organizations/${org.id}/logoUrl` : '';

            const $card = $(`
                <div class="org-card" data-org-id="${org.id}">
                    <div class="org-card-checkmark">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                    </div>
                    <div class="org-card-avatar">
                        ${logoUrl ? `<img class="org-card-logo" src="${logoUrl}" alt="${org.name}" />` : ''}
                        <span class="org-card-initials">${initials}</span>
                    </div>
                    <div class="org-card-name">${org.name}</div>
                </div>
            `);

            // Handle image load/error for proper fallback
            if (logoUrl) {
                const $avatar = $card.find('.org-card-avatar');
                const $img = $card.find('.org-card-logo');
                const $initials = $card.find('.org-card-initials');

                $img.on('load', function() {
                    // Logo loaded successfully - remove background and hide initials
                    $avatar.addClass('has-logo');
                    $initials.hide();
                });

                $img.on('error', function() {
                    // Logo failed to load - remove img and show initials with background
                    $(this).remove();
                    $initials.show();
                });
            }

            $card.on('click', function() {
                $('.org-card').removeClass('selected');
                $(this).addClass('selected');
                selectedValue = org.id;

                if (changeCallback) {
                    changeCallback(org.id);
                }
            });

            $container.append($card);
        });
    };

    return {
        renderOrganizations: renderCards,
        getValue: () => selectedValue,
        onChange: (callback: (value: string) => void) => {
            changeCallback = callback;
        }
    };
};

const reset = () => {
    $('#status-wrap').hide();
    $('#organization-selection').hide();
    $('#auto-select-container').hide();
    $('#error-container').hide();
    $('.error').text('').hide();
}

const showAutoSelectCountdown = (
    organizationId: string,
    organizationName: string,
    host: string,
    onContinue: () => void,
    onCancel: () => void
): void => {
    reset();
    $('#auto-select-container').show();
    $('#auto-select-org-name').text(organizationName);

    // Set organization logo or initials
    const $logoContainer = $('#auto-select-logo-container');
    const initials = getInitials(organizationName);

    if (host) {
        const logoUrl = `${host}/v1/public/organizations/${organizationId}/logoUrl`;
        $logoContainer.html(`
            <div class="auto-select-avatar">
                <img class="auto-select-logo" src="${logoUrl}" alt="${organizationName}" />
                <span class="auto-select-initials">${initials}</span>
            </div>
        `);

        // Handle image load/error
        const $img = $logoContainer.find('.auto-select-logo');
        const $avatar = $logoContainer.find('.auto-select-avatar');
        const $initials = $logoContainer.find('.auto-select-initials');

        $img.on('load', function() {
            $avatar.addClass('has-logo');
            $initials.hide();
        });

        $img.on('error', function() {
            $(this).remove();
            $initials.show();
        });
    } else {
        // No host - show initials only
        $logoContainer.html(`
            <div class="auto-select-avatar">
                <span class="auto-select-initials">${initials}</span>
            </div>
        `);
    }

    let countdown = 3;
    let intervalId: number | null = null;

    const updateCountdown = () => {
        $('#auto-select-countdown-number').text(countdown.toString());
        countdown--;

        if (countdown < 0) {
            if (intervalId !== null) {
                clearInterval(intervalId);
            }
            onContinue();
        }
    };

    // Set initial countdown
    updateCountdown();

    // Start countdown timer
    intervalId = window.setInterval(updateCountdown, 1000);

    // Handle cancel button
    $('#auto-select-cancel-button').off('click').on('click', () => {
        if (intervalId !== null) {
            clearInterval(intervalId);
        }
        onCancel();
    });
};

const showError = (message: string) => {
    reset();
    $('#error-text').text(message);
    $('#error-container').show();
}

const isValidAuth0Domain = (domain: string): boolean => {
    const allowedDomains = [
        'supbubble-dev.us.auth0.com',
        'supbubble-staging.us.auth0.com',
        'supbubble-prod.us.auth0.com',
        'id.dev.liveswitch.com',
        'id.staging.liveswitch.com',
        'id.liveswitch.com'
    ];
    return allowedDomains.includes(domain);
}

const getUrlParams = () => {
    // Auth0 passes 'state' as a query parameter (?state=...)
    // We pass organizations, auth0_domain, email, host via hash (#...) to avoid CloudFront restrictions
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);

    return {
        state: urlParams.get('state') || '',
        auth0_domain: hashParams.get('auth0_domain') || '',
        email: hashParams.get('email') || '',
        host: hashParams.get('host') || '',
        show_countdown: hashParams.get('show_countdown') === 'true',
        app_name: hashParams.get('app_name') || '',
        app_logo: hashParams.get('app_logo') || '',
        app_primary_color: hashParams.get('app_primary_color') || ''
    };
}

const getOrganizationsFromHash = (): Organization[] => {
    try {
        const hash = window.location.hash.substring(1); // Remove the '#'
        const hashParams = new URLSearchParams(hash);
        const orgsParam = hashParams.get('organizations');

        if (orgsParam) {
            return JSON.parse(decodeURIComponent(orgsParam));
        }

        return [];
    } catch (e) {
        console.error('Failed to parse organizations:', e);
        return [];
    }
}

const continueWithOrganization = (orgId: string, state: string, auth0Domain: string) => {
    // Redirect back to Auth0 with selected organization
    // The state parameter is required to continue the Auth0 flow
    // Use the Auth0 domain passed from the Auth0 action
    const continueUrl = `https://${auth0Domain}/continue?state=${encodeURIComponent(state)}&selected_organization_id=${encodeURIComponent(orgId)}`;
    window.location.href = continueUrl;
}

// Initialize when DOM is ready
$(() => {
    try {
        const { state, auth0_domain, email, host, show_countdown, app_name, app_logo, app_primary_color } = getUrlParams();

        if (!state) {
            showError('Missing state parameter. Please restart the authentication process.');
            return;
        }

        if (!auth0_domain) {
            showError('Missing Auth0 domain. Please restart the authentication process.');
            return;
        }

        if (!isValidAuth0Domain(auth0_domain)) {
            showError('Invalid Auth0 domain. Please contact support.');
            return;
        }

        const organizations = getOrganizationsFromHash().sort((a, b) => a.name.localeCompare(b.name));

        if (organizations.length === 0) {
            showError('No organizations available. Please contact support.');
            return;
        }

        // Update primary color if app_primary_color is provided
        if (app_primary_color) {
            // Helper function to adjust color brightness by a percentage
            // Takes a hex color (e.g., "#0070FF") and adjusts brightness
            // Positive percent = lighter, negative percent = darker
            const adjustColor = (color: string, percent: number): string => {
                // Remove '#' and convert hex string to decimal number
                const num = parseInt(color.replace('#', ''), 16);

                // Convert percentage to RGB adjustment amount (0-255 scale)
                const amt = Math.round(2.55 * percent);

                // Extract and adjust each RGB component, clamping between 0-255
                const R = Math.max(0, Math.min(255, (num >> 16) + amt));           // Red channel
                const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt)); // Green channel
                const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));      // Blue channel

                // Recombine RGB values back into hex color string
                return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
            };

            // Set the primary color and generate darker variants for hover/active states
            document.documentElement.style.setProperty('--primary-color', app_primary_color);
            document.documentElement.style.setProperty('--primary-hover', adjustColor(app_primary_color, -10));   // 10% darker
            document.documentElement.style.setProperty('--primary-active', adjustColor(app_primary_color, -20));  // 20% darker
        }

        // Update title and logo if app_name or app_logo are provided
        if (app_name) {
            $('#org-selection-title').text(`Select Your Organization to Log In to ${app_name}`);
        }

        if (app_logo) {
            const $logoImg = $('#organization-selection .logo');
            const originalSrc = $logoImg.attr('src') || '/assets/liveswitch-l-white.svg';

            $logoImg.attr('src', app_logo);

            // Fallback to original logo if app_logo fails to load
            $logoImg.on('error', function() {
                $(this).off('error'); // Prevent infinite loop
                $(this).attr('src', originalSrc);
            });
        }

        // Check cache if email is available
        if (email) {
            const cachedSelection = getCachedOrgSelection(auth0_domain, email);
            if (isCacheValid(cachedSelection, organizations)) {
                console.log('Using cached org selection:', cachedSelection?.organizationName);

                // If show_countdown is true, show countdown UI
                if (show_countdown) {
                    showAutoSelectCountdown(
                        cachedSelection!.organizationId,
                        cachedSelection!.organizationName,
                        host,
                        () => {
                            // On continue - redirect to Auth0
                            continueWithOrganization(cachedSelection!.organizationId, state, auth0_domain);
                        },
                        () => {
                            // On cancel - clear cache and show UI
                            clearOrgCache(auth0_domain, email);
                            $('#status-wrap').show();
                            $('#status').text('Loading organizations...');

                            setTimeout(() => {
                                showOrgSelectionUI(organizations, state, auth0_domain, email, host);
                            }, 500);
                        }
                    );
                } else {
                    // Redirect without countdown
                    continueWithOrganization(cachedSelection!.organizationId, state, auth0_domain);
                }
                return;
            }
        }

        $('#status-wrap').show();
        $('#status').text('Loading organizations...');

        setTimeout(() => {
            showOrgSelectionUI(organizations, state, auth0_domain, email, host);
        }, 500);

    } catch (e) {
        console.error('Error loading organizations:', e);
        showError((e as Error).message || 'An unexpected error occurred');
    }
});

const showOrgSelectionUI = (
    organizations: Organization[],
    state: string,
    auth0_domain: string,
    email: string,
    host: string
): void => {
    // Show organization selection UI
    reset();
    $('#organization-selection').show();

    const orgCardGrid = createOrgCardGrid('#org-card-grid', host);

    orgCardGrid.onChange((value: string) => {
        $('#org-selection-button').prop('disabled', !value);
    });

    // Render organizations
    orgCardGrid.renderOrganizations(organizations);

    // Handle continue button
    $('#org-selection-button').off('click').on('click', () => {
        const orgId = orgCardGrid.getValue();
        if (!orgId) {
            alert('Please select an organization');
            return;
        }

        // Find org name for cache
        const selectedOrg = organizations.find(o => o.id === orgId);
        const orgName = selectedOrg?.name || '';

        // Save to cache if email is available
        if (email && orgName) {
            setCachedOrgSelection(auth0_domain, email, orgId, orgName);
        }

        $('#org-selection-button').prop('disabled', true);
        $('#status').text('Continuing with selected organization...');

        continueWithOrganization(orgId, state, auth0_domain);
    });

    // Error close button
    $('#error-close-button').off('click').on('click', () => {
        window.close();
    });
};

export { }
